import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { forceUpdateReload } from '../lib/appUpdate';
import { pushToast } from '../lib/toastStore';
import './UpdatePrompt.css';

// The build id this tab is running (injected at build time — date·gitsha).
// Human-meaningful so Alex can tell which version he's on (2026-06-23).
const MY_VERSION = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev';

// The deployed build id — read from <meta name="build"> in the LIVE index.html.
// Bypasses every cache (HTTP + SW) so we see what's actually deployed, and is
// a reliable "new version?" signal that does NOT depend on the service-worker
// reaching its `waiting` state (flaky on the cloud2.mac preview).
async function fetchServerMeta(): Promise<{ version: string | null; changelog: string | null }> {
  try {
    const html = await fetch(`/index.html?_=${Date.now()}`, { cache: 'no-store' }).then((r) => r.text());
    const v = html.match(/<meta\s+name=["']build["']\s+content=["']([^"']+)["']/i);
    const c = html.match(/<meta\s+name=["']changelog["']\s+content=["']([^"']*)["']/i);
    // Decode the few entities the build escapes into the attribute.
    const decode = (s: string) => s.replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
    return { version: v ? v[1] : null, changelog: c && c[1] ? decode(c[1]) : null };
  } catch {
    return { version: null, changelog: null };
  }
}

// Non-intrusive "new version available" toast. Two independent triggers, either
// fires it: (1) the PWA service-worker reaching `waiting`, (2) the index.html
// hash poll. The reload path prefers the SW skip-waiting when that's the source
// (proper PWA activation), else a plain reload.
export function UpdatePrompt() {
  // We use useRegisterSW only for its SW-registration + periodic update() checks;
  // the "new version?" DECISION is the version poll below, not the (flaky) waiting
  // state — see hasNewer.
  useRegisterSW({
    onRegisteredSW(_swUrl, reg) {
      if (!reg) return;
      const check = () => { reg.update().catch(() => {}); };
      check();                               // immediately, not only later
      setTimeout(check, 4000);               // catch a deploy that just landed
      setInterval(check, 5 * 60 * 1000);     // every 5 min (was 30 — too slow)
      const onFocus = () => { if (document.visibilityState === 'visible') check(); };
      document.addEventListener('visibilitychange', onFocus);
      window.addEventListener('focus', onFocus);
    },
  });

  // SW-independent version poll.
  const [versionStale, setVersionStale] = useState(false);
  const [newVersion, setNewVersion] = useState<string | null>(null);
  const [newChangelog, setNewChangelog] = useState<string | null>(null);
  useEffect(() => {
    let stopped = false;
    const check = async () => {
      const { version: server, changelog } = await fetchServerMeta();
      if (!stopped && server && server !== MY_VERSION) {
        setNewVersion(server);
        setNewChangelog(changelog);
        setVersionStale(true);
      }
    };
    void check();
    const id = window.setInterval(check, 60 * 1000);
    const onFocus = () => { if (document.visibilityState === 'visible') void check(); };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      stopped = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // Post-update confirmation (#141) → shared store as a GREEN auto-hide toast (#194):
  // forceUpdateReload left a breadcrumb; show "✓ Aktualisiert auf vX" once.
  useEffect(() => {
    try {
      if (localStorage.getItem('ark-just-updated') === '1') {
        localStorage.removeItem('ark-just-updated');
        pushToast({ variant: 'success', dismiss: 'auto', dedupKey: 'updated', title: `Aktualisiert auf ${MY_VERSION}` });
      }
    } catch { /* ignore */ }
  }, []);

  // A genuinely newer build MUST be confirmed by the version poll (the live
  // index.html <meta build> differs from ours). The SW `needRefresh` alone is NOT
  // trusted: after an update a `waiting` worker for the SAME build often lingers,
  // which fired "Neue Version verfügbar — du hast 0.595" right next to "Aktualisiert
  // auf 0.595" (Alex 2026-07-14, nonsense). needRefresh only nudges an extra poll.
  const hasNewer = !!newVersion && newVersion !== MY_VERSION;

  // "Neue Version verfügbar" → shared store as a BLUE, manual-dismiss toast whose
  // primary action is "Neu laden" (#194). dedupKey collapses re-detections; dismissing
  // it (X) won't re-appear unless a newer version is detected. forceUpdateReload takes
  // the hard cache-busting path (unregister SW + clear caches) — the PWA skip-waiting
  // reload is unreliable on iOS (re-serves the OLD bundle).
  useEffect(() => {
    if (!hasNewer) return;
    // Seen-state (#216): dismissing the card persists the TARGET version — the same
    // target never re-toasts (a genuinely newer version still does). And the "Neu:"
    // changelog line only renders when its TEXT is one Alex hasn't seen yet — builds
    // shipped without a fresh changelog entry repeat the old line, which read as
    // "die Message habe ich schon gesehen, warum wieder".
    try {
      if (newVersion && localStorage.getItem('ark-update-dismissed') === newVersion) return;
    } catch { /* storage blocked → just show */ }
    let changelogUnseen = false;
    try { changelogUnseen = !!newChangelog && localStorage.getItem('ark-changelog-seen') !== newChangelog; }
    catch { changelogUnseen = !!newChangelog; }
    const markSeen = () => {
      try {
        if (newVersion) localStorage.setItem('ark-update-dismissed', newVersion);
        if (newChangelog) localStorage.setItem('ark-changelog-seen', newChangelog);
      } catch { /* ignore */ }
    };
    pushToast({
      variant: 'info',
      dismiss: 'manual',
      dedupKey: 'update-available',
      title: 'Neue Version verfügbar',
      onClose: markSeen,
      content: (
        <div className="up-toast">
          <span className="up-ver">{MY_VERSION} → <b>{newVersion}</b></span>
          {changelogUnseen && <span className="up-changelog">Neu: {newChangelog}</span>}
          <button
            type="button"
            className="up-reload"
            onClick={(e) => { e.stopPropagation(); markSeen(); void forceUpdateReload(); }}
          >
            Neu laden
          </button>
        </div>
      ),
    });
  }, [hasNewer, newVersion, newChangelog]);

  return null; // rendering handled by the shared <GlobalToasts> host (#194)
}
